// import { Route, Routes } from "react-router-dom";
// import PageRender from "./PageRender";
// import Index from "./pages/index";
// import useAuthStore from "./data/Stores/Authstore";
// import Home from "./screens/home";
// import { ToastContainer } from "react-toastify";
// import Sidebar from "./components/sidebar/Sidebar";
// import Navbar from "./components/navbar/Navbar";

// // import { clearErrors } from "./data/Reducers/ErrorReducer";

// const Routers = () => {
//   const { isAuth } = useAuthStore();

//   return (
//     <>
//       <ToastContainer position="bottom-left" />

//       <Routes>
//         <Route
//           path="/"
//           element={!isAuth ? <Index /> : <Home />}
//         />
//         <Route
//           path="/:page"
//           element={<PageRender />}
//         />
//         <Route
//           path="/:page/:id"
//           element={<PageRender />}
//         />
//         <Route
//           path="/:page/:id/:step"
//           element={<PageRender />}
//         />
//       </Routes>
//     </>
//   );
// };

// export default Routers;

import { useContext, useEffect, useState } from "react";
import { Route, Routes } from "react-router-dom";
import { Navigate, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import PageRender from "./PageRender";
import Index from "./pages/index";
import { useSelector } from "react-redux";
import Navbar from "./components/navbar/Navbar.jsx";
import Sidebar from "./components/sidebar/Sidebar.jsx";
import useAuthBootstrap from "./hooks/useAuthBootstrap";
import useSessionExpired from "./hooks/useSessionExpired";
import { hasStoredSession } from "./data/authStorage";
import InviteOnboarding from "./screens/invite-onboarding.jsx";
import AdminQueries from "./pages/query-management/admin-queries.jsx";
import EmployeeQueries from "./pages/query-management/employee-queries.jsx";
import QueryDetails from "./pages/query-management/query-details.jsx";
import FetchLoadingState from "./components/feedback/FetchLoadingState.jsx";
import RouteErrorBoundary from "./components/feedback/RouteErrorBoundary.jsx";
import PublicGuarantorForm from "./screens/guarantor-form.jsx";
import Reporting from "./pages/reporting";
import SupportCenter from "./pages/support-center";
import FeatureAccessGate from "./components/subscription/FeatureAccessGate";
import LearningManagementPage from "./pages/learning-management";
import EWallet from "./pages/e-wallet";
import PayrollPage from "./pages/payroll";
import TalentManagementPage from "./pages/talent-management";
import OrganizationSetup from "./pages/organization-setup";
import ContractStaffForm from "./pages/contract-staff/form";
import ContractWorkPage from "./pages/contract-staff/work";
import Careers from "./screens/careers";
import PublicJob from "./screens/public-job-upload";
import TalentClientPortal from "./screens/talent-client-portal";
import CandidateOffer from "./screens/candidate-offer";
import { GlobalState } from "./data/Context";
import { isCompanyAccount } from "./data/authStorage";
import { getOrganizationSetup } from "./api/organizationSetup";

const Routers = () => {
  const auth = useSelector((state) => state.auth);
  const { organization } = useContext(GlobalState);
  const isBootstrapped = useAuthBootstrap();
  useSessionExpired(); // Listen for session expiration
  const { pathname } = useLocation();
  const normalizedPath = `${pathname || "/"}`.replace(/\/+$/, "") || "/";
  const isAuthenticated = !!auth?.isAuth || hasStoredSession();
  const isOrganizationAccount = isCompanyAccount(organization);
  const [setupStatus, setSetupStatus] = useState({ loading: false, completed: false, error: "" });
  const [setupAttempt, setSetupAttempt] = useState(0);
  const isAuthScreen = ["/login", "/sign-up", "/signup", "/register", "/forgot-password", "/reset-password"].includes(
    normalizedPath.toLowerCase(),
  );
  const isInviteOnboardingRoute =
    normalizedPath.toLowerCase() === "/invite/onboarding" ||
    normalizedPath.toLowerCase() === "/invite/onboarding/index";
  const isPublicGuarantorRoute = normalizedPath.toLowerCase().startsWith("/guarantor-form/");
  const isPublicCareersRoute = normalizedPath.toLowerCase().startsWith("/careers/");
  const isPublicTalentRoute = normalizedPath.toLowerCase().startsWith("/talent-client/") || normalizedPath.toLowerCase().startsWith("/candidate-offer/");

  useEffect(() => {
    if (!isAuthenticated || !isOrganizationAccount) {
      setSetupStatus({ loading: false, completed: false, error: "" });
      return undefined;
    }
    let active = true;
    setSetupStatus({ loading: true, completed: false, error: "" });
    getOrganizationSetup()
      .then((setup) => { if (active) setSetupStatus({ loading: false, completed: Boolean(setup?.completed), error: "" }); })
      .catch(() => { if (active) setSetupStatus({ loading: false, completed: false, error: "Unable to confirm your organization setup status." }); });
    return () => { active = false; };
  }, [isAuthenticated, isOrganizationAccount, organization?.id, setupAttempt]);

  useEffect(() => {
    const markSetupComplete = () => setSetupStatus({ loading: false, completed: true, error: "" });
    window.addEventListener("organizationSetupCompleted", markSetupComplete);
    return () => window.removeEventListener("organizationSetupCompleted", markSetupComplete);
  }, []);

  if (!isBootstrapped) {
    return <FetchLoadingState message="Loading your workspace..." fullScreen className="bg-[#FAFAFA] font-Outfit" />;
  }

  const appRoutes = (
    <Routes>
      <Route
        path="/"
        element={
          isAuthenticated ? (
            <Index />
          ) : (
            <Navigate
              to="/login"
              replace
            />
          )
        }
      />
      {/* Query Management Routes */}
      <Route path="/payroll/*" element={<FeatureAccessGate featureKey="payroll" title="Payroll Suite"><PayrollPage /></FeatureAccessGate>} />
      <Route path="/e-wallet/*" element={<FeatureAccessGate featureKey="wallet" title="Employee Wallets"><EWallet /></FeatureAccessGate>} />
      <Route path="/talent-management/*" element={<FeatureAccessGate featureKey="talent" title="Talent Management"><TalentManagementPage /></FeatureAccessGate>} />
      <Route path="/reporting/intelligence" element={<FeatureAccessGate featureKey="intelligence" title="People Intelligence"><Reporting /></FeatureAccessGate>} />
      <Route path="/reporting/recommendations" element={<FeatureAccessGate featureKey="intelligence" title="People Intelligence"><Reporting /></FeatureAccessGate>} />
      <Route path="/reporting/*" element={<Reporting />} />
      <Route path="/support" element={<SupportCenter />} />
      <Route path="/learning-management" element={<FeatureAccessGate featureKey="learning-management-system" title="Learning Management System (LMS)"><LearningManagementPage /></FeatureAccessGate>} />
      <Route path="/organization-setup" element={<OrganizationSetup />} />
      <Route path="/contract-staff/new" element={<ContractStaffForm mode="create" />} />
      <Route path="/contract-staff/:employeeId" element={<ContractWorkPage />} />
      <Route path="/contract-staff/:employeeId/edit" element={<ContractStaffForm mode="edit" />} />
      <Route path="/contract-staff/:employeeId/renew" element={<ContractStaffForm mode="renew" />} />
      <Route path="/contract-staff/:employeeId/work" element={<ContractWorkPage />} />
      <Route
        path="/query-management/admin"
        element={<AdminQueries />}
      />
      <Route
        path="/query-management/my-queries"
        element={<EmployeeQueries />}
      />
      <Route
        path="/query-management/query-details/:queryId"
        element={<QueryDetails />}
      />
      {/* Catch-all routes */}
      <Route
        path="/:page"
        element={<RouteErrorBoundary><PageRender /></RouteErrorBoundary>}
      />
      <Route
        path="/:page/:id"
        element={<RouteErrorBoundary><PageRender /></RouteErrorBoundary>}
      />
      <Route
        path="/:page/:id/:step"
        element={<RouteErrorBoundary><PageRender /></RouteErrorBoundary>}
      />
    </Routes>
  );

  if (isAuthScreen) {
    if (isAuthenticated) {
      return (
        <>
          <ToastContainer position="bottom-center" />
          <Navigate
            to="/"
            replace
          />
        </>
      );
    }

    return (
      <>
        <ToastContainer position="bottom-center" />
        {appRoutes}
      </>
    );
  }

  if (isInviteOnboardingRoute) {
    return (
      <>
        <ToastContainer position="bottom-center" />
        <Routes>
          <Route
            path="/invite/onboarding"
            element={<InviteOnboarding />}
          />
        </Routes>
      </>
    );
  }

  if (isPublicGuarantorRoute) {
    return <><ToastContainer position="bottom-center" /><Routes><Route path="/guarantor-form/:token" element={<PublicGuarantorForm />} /></Routes></>;
  }

  if (isPublicCareersRoute) {
    return <><ToastContainer position="bottom-center" /><Routes><Route path="/careers/:slug" element={<Careers />} /><Route path="/careers/jobs/:slug" element={<PublicJob />} /></Routes></>;
  }

  if (isPublicTalentRoute) {
    return <><ToastContainer position="bottom-center" /><Routes><Route path="/talent-client/:token" element={<TalentClientPortal />} /><Route path="/candidate-offer/:token" element={<CandidateOffer />} /></Routes></>;
  }

  if (isAuthenticated && isOrganizationAccount) {
    if (setupStatus.loading) {
      return <FetchLoadingState message="Checking your organization setup..." fullScreen className="bg-[#FAFAFA] font-Outfit" />;
    }
    if (setupStatus.error) {
      return <main className="grid min-h-screen place-items-center bg-slate-50 p-6 font-Outfit"><div className="max-w-md rounded-2xl border bg-white p-6 text-center shadow-sm"><h1 className="text-lg font-bold text-slate-900">Workspace setup status unavailable</h1><p className="mt-2 text-sm text-slate-600">{setupStatus.error} Your workspace menus will remain locked until we can verify setup.</p><button type="button" onClick={() => setSetupAttempt((attempt) => attempt + 1)} className="mt-4 rounded-xl bg-[#1234CD] px-4 py-2.5 text-sm font-semibold text-white">Try again</button></div></main>;
    }
    if (!setupStatus.completed) {
      return <><ToastContainer position="bottom-center" /><Routes><Route path="/organization-setup" element={<OrganizationSetup />} /><Route path="*" element={<Navigate to="/organization-setup" replace />} /></Routes></>;
    }
    if (normalizedPath === "/organization-setup") return <Navigate to="/dashboard" replace />;
  }

  if (!isAuthenticated) {
    return (
      <>
        <ToastContainer position="bottom-center" />
        <Navigate
          to="/login"
          replace
        />
      </>
    );
  }

  return (
    <>
      <ToastContainer position="bottom-center" />
      <Sidebar>
        <div>
          <Navbar />
        </div>

        {appRoutes}
      </Sidebar>
    </>
  );
};

export default Routers;
